import { create } from 'zustand'
import { localStore } from '../lib/platform'

const STORAGE_KEY = 'boss-chat.theme.v1'

/** Follow the phone, or override it on this device. */
export type Theme = 'system' | 'light' | 'dark'

const THEMES: Theme[] = ['system', 'light', 'dark']

export function loadTheme(storage: Pick<Storage, 'getItem'> | undefined = localStore()): Theme {
  try {
    const stored = storage?.getItem(STORAGE_KEY)
    return THEMES.includes(stored as Theme) ? (stored as Theme) : 'system'
  } catch {
    return 'system'
  }
}

function deviceIsDark(): boolean {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches === true
}

/**
 * Marks the page light or dark. 'system' is resolved here rather than in the stylesheet, so the dark palette is
 * written once: the same attribute answers a phone's own setting and a choice made in Settings.
 */
export function applyTheme(theme: Theme): void {
  const dark = theme === 'dark' || (theme === 'system' && deviceIsDark())
  document.documentElement.dataset.theme = dark ? 'dark' : 'light'
  // The browser's own chrome (the status bar on a phone) follows the app.
  for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
    if (!meta.getAttribute('media')) meta.content = dark ? '#10161f' : '#ffffff'
  }
}

/** While nobody has chosen, the app turns with the phone. */
export function watchDeviceTheme(): () => void {
  const query = window.matchMedia?.('(prefers-color-scheme: dark)')
  const follow = () => {
    if (useTheme.getState().theme === 'system') applyTheme('system')
  }
  query?.addEventListener('change', follow)
  return () => query?.removeEventListener('change', follow)
}

interface ThemeState {
  theme: Theme
  choose: (theme: Theme) => void
}

export const useTheme = create<ThemeState>((set) => ({
  theme: loadTheme(),
  choose: (theme) => {
    try {
      if (theme === 'system') localStore()?.removeItem(STORAGE_KEY)
      else localStore()?.setItem(STORAGE_KEY, theme)
    } catch {
      // Storage unavailable: the choice holds until the app is closed.
    }
    applyTheme(theme)
    set({ theme })
  },
}))
